/**
 * Order-level surcharge decisions for a quote's cart.
 *
 * A cart consisting ONLY of `exemptFromOrderCharges` products (the Measurement
 * Fee) is billed at face value: no minimum-order top-up, no delivery fee, no
 * CBD surcharge. Any non-exempt line re-applies all three to the whole order.
 *
 * A cart consisting ONLY of adhoc (non-catalog, materials-only supply) lines
 * waives just the minimum-order top-up — delivery fee and CBD surcharge still
 * apply since materials are still physically delivered. Any catalog line in
 * the cart re-applies the minimum-order top-up as normal.
 *
 * Pure + node-tested; consumed by `useQuoteSummary`.
 */
import { GLOBAL_SETTINGS } from '../data/settings';

interface ChargeItem {
  product?: { exemptFromOrderCharges?: boolean } | null;
  source?: 'catalog' | 'adhoc';
}

export function isAllExempt(items: ChargeItem[]): boolean {
  return items.length > 0 && items.every((i) => i.product?.exemptFromOrderCharges === true);
}

export function isAllAdhoc(items: ChargeItem[]): boolean {
  return items.length > 0 && items.every((i) => i.source === 'adhoc');
}

export interface OrderCharges {
  minChargeApplied: boolean;
  effectiveItemsTotal: number;
  deliveryFee: number;
  cbdSurchargeFee: number;
}

export function computeOrderCharges(params: {
  items: ChargeItem[];
  itemsTotal: number;
  cbdSurcharge: boolean;
  deliveryWaived: boolean;
}): OrderCharges {
  const { items, itemsTotal, cbdSurcharge, deliveryWaived } = params;
  const allExempt = isAllExempt(items);
  const allAdhoc = isAllAdhoc(items);
  const minChargeApplied =
    !allExempt && !allAdhoc && itemsTotal < GLOBAL_SETTINGS.minOrderCharge && itemsTotal > 0;
  const effectiveItemsTotal = minChargeApplied ? GLOBAL_SETTINGS.minOrderCharge : itemsTotal;
  const deliveryFee =
    !allExempt && itemsTotal > 0 && !deliveryWaived ? GLOBAL_SETTINGS.deliveryFee : 0;
  const cbdSurchargeFee =
    !allExempt && cbdSurcharge && itemsTotal > 0 ? GLOBAL_SETTINGS.cbdSurcharge : 0;
  return { minChargeApplied, effectiveItemsTotal, deliveryFee, cbdSurchargeFee };
}
